'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/core/i18n/navigation';
import { BrandLogo, LocaleSelector, SignUser, SmartIcon, ThemeToggler } from '@/shared/blocks/common';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger as RawNavigationMenuTrigger } from '@/shared/components/ui/navigation-menu';
import { useMedia } from '@/shared/hooks/use-media';
import { cn } from '@/shared/lib/utils';
import { NavItem } from '@/shared/types/blocks/common';
import { Header as HeaderType } from '@/shared/types/blocks/landing';

// For Next.js hydration mismatch warning, conditionally render NavigationMenuTrigger only after mount to avoid inconsistency between server/client render
function NavigationMenuTrigger(props) {
  const $ = _c(4);
  const [mounted, setMounted] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!mounted) {
    return null;
  }
  let t2;
  if ($[2] !== props) {
    t2 = <RawNavigationMenuTrigger {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function Header({
  header
}: {
  header: HeaderType;
}) {
  const t = useTranslations('landing.header.announcement');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const isScrolledRef = useRef(false);
  const isBannerVisibleRef = useRef(true);
  const scrollRafRef = useRef<number | null>(null);
  const isLarge = useMedia('(min-width: 64rem)');
  const pathname = usePathname();
  const isGeneratorLandingPage = ['/free-ai-dance-generator', '/ai-baby-dance-generator'].some(path => pathname.endsWith(path));
  const getHeaderHref = (url?: string) => {
    if (!url || !isGeneratorLandingPage) {
      return url || '';
    }
    const localAnchors = new Set(['/#dance-templates', '/#generator', '/#how-to-use']);
    if (localAnchors.has(url)) {
      return url.slice(1);
    }
    return url;
  };
  useEffect(() => {
    // Listen to scroll event to enable header styles on scroll
    const handleScroll = () => {
      // Coalesce high-frequency scroll events & only update state when value changes.
      if (scrollRafRef.current != null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const scrollY = window.scrollY;
        const next = scrollY > 50;
        if (next !== isScrolledRef.current) {
          isScrolledRef.current = next;
          setIsScrolled(next);
        }
        const nextBannerVisible = scrollY <= 0;
        if (nextBannerVisible !== isBannerVisibleRef.current) {
          isBannerVisibleRef.current = nextBannerVisible;
          setIsBannerVisible(nextBannerVisible);
        }
      });
    };

    // Initialize once on mount.
    handleScroll();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  // Navigation menu for large screens
  const NavMenu = () => {
    return <NavigationMenu viewport={false} className="**:data-[slot=navigation-menu-content]:top-10 max-lg:hidden">
        <NavigationMenuList className="gap-2">
          {header.nav?.items?.map((item, idx) => {
          if (!item.children || item.children.length === 0) {
            return <NavigationMenuItem key={idx}>
                  <NavigationMenuLink asChild>
                    <Link href={getHeaderHref(item.url)} target={item.target || '_self'} className={`flex flex-row items-center gap-2 px-4 py-1.5 text-sm ${item.is_active || pathname.endsWith(item.url as string) ? 'bg-muted/40 text-muted-foreground' : ''}`}>
                      {item.icon && <SmartIcon name={item.icon as string} />}
                      {item.title}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>;
          }
          return <NavigationMenuItem key={idx}>
                <NavigationMenuTrigger className="flex flex-row items-center gap-2 text-sm">
                  {item.icon && <SmartIcon name={item.icon as string} className="h-4 w-4" />}
                  {item.title}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="min-w-2xs origin-top p-0.5">
                  <div className="border-foreground/5 bg-card ring-foreground/5 rounded-[calc(var(--radius)-2px)] border border-transparent p-2 shadow ring-1">
                    <ul className="mt-1 space-y-2">
                      {item.children?.map((subItem: NavItem, index: number) => <ListItem key={index} href={getHeaderHref(subItem.url)} target={subItem.target || '_self'} title={subItem.title || ''} description={subItem.description || ''}>
                          {subItem.icon && <SmartIcon name={subItem.icon as string} />}
                        </ListItem>)}
                    </ul>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>;
        })}
        </NavigationMenuList>
      </NavigationMenu>;
  };

  // Mobile menu using Accordion, shown on small screens
  const MobileMenu = ({
    closeMenu
  }: {
    closeMenu: () => void;
  }) => {
    return <nav role="navigation" className="w-full [--color-border:--alpha(var(--color-foreground)/5%)] [--color-muted:--alpha(var(--color-foreground)/5%)]">
        <Accordion type="single" collapsible className="-mx-4 mt-0.5 space-y-0.5 **:hover:no-underline">
          {header.nav?.items?.map((item_0, idx_0) => {
          return <AccordionItem key={idx_0} value={item_0.title || ''} className="group relative border-b-0 before:pointer-events-none before:absolute before:inset-x-4 before:bottom-0 before:border-b">
                {item_0.children && item_0.children.length > 0 ? <>
                    <AccordionTrigger className="data-[state=open]:bg-muted flex items-center justify-between px-4 py-3 text-lg **:!font-normal">
                      {item_0.title}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5">
                      <ul>
                        {item_0.children?.map((subItem_0: NavItem, iidx) => <li key={iidx}>
                            <Link href={getHeaderHref(subItem_0.url)} onClick={closeMenu} className="grid grid-cols-[auto_1fr] items-center gap-2.5 px-4 py-2">
                              <div aria-hidden className="flex items-center justify-center *:size-4">
                                {subItem_0.icon && <SmartIcon name={subItem_0.icon as string} />}
                              </div>
                              <div className="text-base">{subItem_0.title}</div>
                            </Link>
                          </li>)}
                      </ul>
                    </AccordionContent>
                  </> : <Link href={getHeaderHref(item_0.url)} onClick={closeMenu} className="data-[state=open]:bg-muted flex items-center justify-between px-4 py-3 text-lg **:!font-normal">
                    {item_0.title}
                  </Link>}
              </AccordionItem>;
        })}
        </Accordion>
      </nav>;
  };

  // List item for submenus in NavigationMenu
  function ListItem({
    title,
    description,
    children,
    href,
    target,
    ...props
  }: React.ComponentPropsWithoutRef<'li'> & {
    href: string;
    title: string;
    description?: string;
    target?: string;
  }) {
    return <li {...props}>
        <NavigationMenuLink asChild>
          <Link href={href} target={target || '_self'} className="grid grid-cols-[auto_1fr] gap-3.5">
            <div className="bg-background ring-foreground/10 relative flex size-9 items-center justify-center rounded border border-transparent shadow-sm ring-1">
              {children}
            </div>
            <div className="space-y-0.5">
              <div className="text-foreground text-sm font-medium">{title}</div>
              <p className="text-muted-foreground line-clamp-1 text-xs">
                {description}
              </p>
            </div>
          </Link>
        </NavigationMenuLink>
      </li>;
  }
  return <>
      <header data-state={isMobileMenuOpen ? 'active' : 'inactive'} {...isScrolled && {
      'data-scrolled': true
    }} className="has-data-[state=open]:bg-background/50 fixed inset-x-0 top-0 z-50 has-data-[state=open]:h-screen has-data-[state=open]:backdrop-blur">
        <div className={cn('absolute inset-x-0 top-0 z-50 h-18 border-transparent ring-1 ring-transparent transition-all duration-300', isBannerVisible && 'h-26 max-lg:h-22', 'in-data-scrolled:border-foreground/5 in-data-scrolled:bg-background/75 in-data-scrolled:border-b in-data-scrolled:backdrop-blur', 'has-data-[state=open]:ring-foreground/5 has-data-[state=open]:bg-card/75 has-data-[state=open]:h-[calc(var(--navigation-menu-viewport-height)+3.4rem)] has-data-[state=open]:border-b has-data-[state=open]:shadow-lg has-data-[state=open]:shadow-black/10 has-data-[state=open]:backdrop-blur', 'max-xl:in-data-[state=active]:bg-background/75 max-xl:in-data-[state=active]:h-auto max-xl:in-data-[state=active]:border-b max-xl:in-data-[state=active]:shadow-lg max-xl:in-data-[state=active]:shadow-black/10 max-xl:in-data-[state=active]:backdrop-blur', 'max-lg:h-14 max-lg:overflow-hidden max-lg:border-b max-lg:in-data-[state=active]:h-screen')}>
          <div className={cn('flex h-8 items-center justify-center border-b border-black/10 bg-[linear-gradient(135deg,oklch(0.88_0.10_18),oklch(0.9_0.06_290),oklch(0.9_0.08_170))] px-4 text-center text-xs font-medium text-black shadow-sm transition-all duration-300 max-lg:hidden dark:border-white/10 dark:bg-[linear-gradient(135deg,oklch(0.45_0.13_18),oklch(0.42_0.08_290),oklch(0.43_0.10_170))] dark:text-white dark:shadow-black/20', !isBannerVisible && 'pointer-events-none h-0 overflow-hidden border-b-0 opacity-0')}>
            <span className="truncate">
              {t('prefix')}{' '}
              <Link href="/pricing" className="text-black underline underline-offset-4 hover:opacity-70 dark:text-white dark:hover:opacity-80">
                {t('link')}
              </Link>
              {t('suffix')}
            </span>
          </div>
          <div className="container">
            <div className="relative flex flex-wrap items-center justify-between lg:py-5 xl:flex-nowrap">
              <div className="flex items-center justify-between gap-8 max-xl:w-full max-lg:h-14 max-lg:border-b">
                {/* Brand Logo */}
                {header.brand && <BrandLogo brand={header.brand} />}

                {/* Desktop Navigation Menu */}
                {isLarge && <NavMenu />}
                {/* Hamburger menu button for mobile navigation */}
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen == true ? 'Close Menu' : 'Open Menu'} className="relative z-20 -m-2.5 -mr-3 block cursor-pointer p-2.5 xl:hidden">
                  <Menu className="m-auto size-5 duration-200 in-data-[state=active]:scale-0 in-data-[state=active]:rotate-180 in-data-[state=active]:opacity-0" />
                  <X className="absolute inset-0 m-auto size-5 scale-0 -rotate-180 opacity-0 duration-200 in-data-[state=active]:scale-100 in-data-[state=active]:rotate-0 in-data-[state=active]:opacity-100" />
                </button>
              </div>

              {/* Show mobile menu if needed */}
              {!isLarge && isMobileMenuOpen && <MobileMenu closeMenu={() => setIsMobileMenuOpen(false)} />}

              {/* Header right section: theme toggler, locale selector, sign, buttons */}
              <div className="mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 in-data-[state=active]:flex max-xl:in-data-[state=active]:mt-6 md:flex-nowrap xl:m-0 xl:flex xl:w-fit xl:gap-6 xl:space-y-0 xl:border-transparent xl:bg-transparent xl:p-0 xl:shadow-none dark:shadow-none dark:xl:bg-transparent">
                <div className="flex w-full flex-row items-center gap-4 sm:flex-row sm:gap-6 sm:space-y-0 md:w-fit">
                  {header.buttons && header.buttons.map((button, idx_1) => <Link key={idx_1} href={getHeaderHref(button.url)} target={button.target || '_self'} className={cn('btn-playful inline-flex items-center justify-center gap-2 text-sm font-bold whitespace-nowrap transition-all focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50', 'h-8 px-4', button.variant === 'outline' ? 'bg-background border-primary/20 hover:bg-primary/5 hover:border-primary/40 border-2' : 'from-primary to-chart-5 soft-shadow bg-gradient-to-r text-white hover:opacity-90')}>
                        {button.icon && <SmartIcon name={button.icon as string} className="size-4" />}
                        <span>{button.title}</span>
                      </Link>)}

                  {header.show_theme ? <ThemeToggler /> : null}
                  {header.show_locale ? <LocaleSelector /> : null}
                  <div className="flex-1 md:hidden"></div>
                  {header.show_sign ? <SignUser userNav={header.user_nav} /> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>;
}